import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { usePortfolioData } from "@/hooks/use-portfolio-data";
import { houseName, tenantName } from "@/lib/ledger";

const SEGMENTS = [
  { value: "", label: "All" },
  { value: "tenant", label: "Tenants" },
  { value: "contractor", label: "Contractors" },
];

export default function Contacts() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const search = params.get("q") || "";
  const { data, isLoading, error } = usePortfolioData(["properties", "tenants", "contractors"]);
  const propertiesById = useMemo(() => new Map(data.properties.map((p) => [String(p._id), p])), [data.properties]);

  const contacts = useMemo(() => {
    const tenants = data.tenants
      .filter((t) => t.isActive)
      .map((t) => ({
        id: `t-${t._id}`,
        type: "tenant",
        name: tenantName(t),
        detail: propertiesById.get(String(t.propertyId)) ? houseName(propertiesById.get(String(t.propertyId))) : "",
        detailLink: `/houses/${t.propertyId}`,
        phone: t.phone,
        email: t.email,
        manage: "/tenants",
      }));
    const contractors = data.contractors.map((c) => ({
      id: `c-${c._id}`,
      type: "contractor",
      name: c.companyName && c.contactName && c.companyName !== c.contactName ? `${c.contactName} · ${c.companyName}` : c.companyName || c.contactName,
      detail: (c.specialty || []).join(", "),
      phone: c.phone,
      email: c.email,
      manage: "/contractors",
    }));
    const query = search.trim().toLowerCase();
    return [...tenants, ...contractors]
      .filter((c) => !type || c.type === type)
      .filter((c) => !query || `${c.name} ${c.detail} ${c.phone} ${c.email}`.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data.tenants, data.contractors, propertiesById, type, search]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <PageLayout
      title="Contacts"
      actions={
        <>
          <Button variant="outline" size="sm" asChild>
            <Link to="/tenants">Add tenant</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/contractors">Add contractor</Link>
          </Button>
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-input bg-background p-0.5">
          {SEGMENTS.map((segment) => (
            <button
              key={segment.value}
              type="button"
              onClick={() => update("type", segment.value)}
              className={`rounded-md px-3 py-1.5 text-sm ${type === segment.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {segment.label}
            </button>
          ))}
        </div>
        <label className="relative min-w-[200px] flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm"
            value={search}
            onChange={(event) => update("q", event.target.value)}
            placeholder="Search name, trade, phone"
            aria-label="Search contacts"
          />
        </label>
      </div>
      {error ? <p className="mb-3 text-sm text-destructive">{error.message}</p> : null}
      <div className="hh-panel overflow-x-auto">
        <table className="hh-table min-w-[680px]">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>House / trade</th>
              <th>Phone</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => (
              <tr key={contact.id}>
                <td className="font-medium">
                  <Link to={contact.manage} className="hover:underline">
                    {contact.name}
                  </Link>
                </td>
                <td className="text-muted-foreground">{contact.type === "tenant" ? "Tenant" : "Contractor"}</td>
                <td className="max-w-[220px] truncate">
                  {contact.detailLink && contact.detail ? (
                    <Link to={contact.detailLink} className="hover:underline">
                      {contact.detail}
                    </Link>
                  ) : (
                    contact.detail || <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {contact.phone ? <a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a> : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="max-w-[220px] truncate">
                  {contact.email ? <a href={`mailto:${contact.email}`} className="hover:underline">{contact.email}</a> : <span className="text-muted-foreground">—</span>}
                </td>
              </tr>
            ))}
            {!contacts.length ? (
              <tr>
                <td colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                  {isLoading ? "Loading…" : "No contacts match."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
